import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Bell,
  BellRing,
  CheckCircle,
  ChevronRight,
  Info,
  X,
  ShieldAlert,
} from 'lucide-react';
import {
  type ReminderSummary,
  type MachineReminder,
  getNotificationPermission,
  requestNotificationPermission,
  dispatchWebNotification,
  dismissReminder,
  isReminderDismissed,
} from '../../services/scheduler';
import { StatusBadge } from './StatusBadge';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';

export interface ReminderBannerProps {
  summary: ReminderSummary;
  onSelectMachine?: (machineId: string) => void;
  onStartInspection?: (machineId: string) => void;
  className?: string;
}

export function ReminderBanner({
  summary,
  onSelectMachine,
  onStartInspection,
  className = '',
}: ReminderBannerProps) {
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [permissionState, setPermissionState] = useState<
    NotificationPermission | 'unsupported'
  >('unsupported');
  const [dismissedLocally, setDismissedLocally] = useState<boolean>(false);

  useEffect(() => {
    setPermissionState(getNotificationPermission());
  }, []);

  // Filter out any reminders temporarily dismissed in this session
  const activeReminders = summary.reminders.filter(
    (r) => !isReminderDismissed(r.machineId)
  );

  // If no machines require attention or banner dismissed locally, don't show banner
  if (activeReminders.length === 0 || dismissedLocally) {
    // If notification permission is default, offer opt-in prompt optionally
    return null;
  }

  const hasOverdue = summary.overdueCount > 0;
  const bannerBg = hasOverdue
    ? 'bg-rose-50 border-rose-300 text-rose-900 shadow-sm'
    : 'bg-amber-50 border-amber-300 text-amber-900 shadow-sm';

  const iconColor = hasOverdue ? 'text-rose-600' : 'text-amber-600';

  const handleRequestPermission = async () => {
    const res = await requestNotificationPermission();
    setPermissionState(res);
    if (res === 'granted' && activeReminders.length > 0) {
      // Trigger a sample alert for the first overdue/due machine
      await dispatchWebNotification(activeReminders[0]);
    }
  };

  return (
    <>
      {/* Floating / Sticky Alert Banner */}
      <div
        className={`rounded-xl border p-3.5 sm:p-4 shadow-sm transition-all animate-in fade-in slide-in-from-top-2 ${bannerBg} ${className}`}
        role="alert"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg bg-white/80 border border-slate-200 shadow-2xs flex-shrink-0 ${iconColor}`}>
              {hasOverdue ? (
                <ShieldAlert className="w-5 h-5 animate-bounce" />
              ) : (
                <BellRing className="w-5 h-5 animate-pulse" />
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-black text-sm sm:text-base text-slate-900 tracking-wide">
                  {hasOverdue
                    ? `🚨 ตรวจพบเครื่องจักรเกินกำหนดบำรุงรักษา (${summary.overdueCount} เครื่อง)`
                    : `⚠️ มีเครื่องจักรใกล้ถึงกำหนดบำรุงรักษา (${summary.dueSoonCount} เครื่อง)`}
                </h3>
                {summary.overdueCount > 0 && summary.dueSoonCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded bg-white/90 border border-slate-200 text-slate-700 font-semibold shadow-2xs">
                    (Overdue: {summary.overdueCount}, Due Soon: {summary.dueSoonCount})
                  </span>
                )}
              </div>

              <p className="text-xs sm:text-sm text-slate-700 mt-1 line-clamp-2">
                {activeReminders[0]?.title}: {activeReminders[0]?.message}
                {activeReminders.length > 1 &&
                  ` และอีก ${activeReminders.length - 1} เครื่องที่ต้องตรวจสอบ`}
              </p>

              {/* Action Buttons inside Banner */}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Button
                  size="sm"
                  variant={hasOverdue ? 'danger' : 'secondary'}
                  onClick={() => setShowDetailModal(true)}
                  leftIcon={<Info className="w-3.5 h-3.5" />}
                >
                  ดูรายละเอียดทั้งหมด ({activeReminders.length})
                </Button>

                {permissionState === 'default' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleRequestPermission}
                    leftIcon={<Bell className="w-3.5 h-3.5" />}
                  >
                    เปิดการแจ้งเตือน PWA
                  </Button>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setDismissedLocally(true)}
            className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-black/5 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center flex-shrink-0"
            aria-label="Dismiss banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Detailed Reminders Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={
          <div className="flex items-center gap-2 text-white">
            <ShieldAlert className="w-5 h-5 text-rose-500" />
            <span>รายการเครื่องจักรที่ต้องได้รับการบำรุงรักษา</span>
          </div>
        }
        maxWidth="lg"
        footer={
          <div className="flex items-center justify-between w-full">
            <span className="text-xs text-slate-400">
              พบ {activeReminders.length} เครื่องที่สถานะไม่ปกติ
            </span>
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
              ปิดหน้าต่าง
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-600">
            ระบบคำนวณความถี่ตามปฏิทิน (Daily/Weekly/Monthly/Yearly)
            และชั่วโมงสะสมของเครื่องจักรตามเกณฑ์มาตรฐาน TOS VARNSDORF
          </p>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {activeReminders.map((reminder) => (
              <div
                key={reminder.id}
                className={`p-3.5 rounded-xl border transition-all shadow-xs ${
                  reminder.status === 'OVERDUE'
                    ? 'bg-rose-50/70 border-rose-200'
                    : 'bg-amber-50/70 border-amber-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900 text-sm sm:text-base">
                        {reminder.machineName}
                      </span>
                      <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-blue-700 font-semibold border border-slate-200">
                        {reminder.machineId}
                      </span>
                      <StatusBadge status={reminder.status} size="sm" />
                    </div>

                    <p className="text-xs text-slate-500 mt-1">
                      รุ่น: {reminder.machineModel} | ชั่วโมงสะสม:{' '}
                      <strong className="text-slate-800">
                        {reminder.cumulativeHours.toLocaleString()} ชม.
                      </strong>
                    </p>

                    <div className="mt-2 text-xs bg-white border border-slate-200 rounded-lg p-2.5 text-slate-700 space-y-1 shadow-2xs">
                      <p className="font-semibold text-rose-700">
                        สาเหตุ: {reminder.message}
                      </p>
                      {reminder.reasons.length > 1 && (
                        <ul className="list-disc list-inside text-slate-600 pl-1">
                          {reminder.reasons.map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {onStartInspection && (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => {
                          setShowDetailModal(false);
                          onStartInspection(reminder.machineId);
                        }}
                        rightIcon={<ChevronRight className="w-3.5 h-3.5" />}
                      >
                        ตรวจเช็ก
                      </Button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        dismissReminder(reminder.machineId);
                        setShowDetailModal(false);
                      }}
                      className="text-[11px] text-slate-500 hover:text-slate-700 underline text-center"
                    >
                      ซ่อนชั่วคราว
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
}
